// ============================================================================
// Safe Care - Configuration des règles d'une version (éligibilité, cotisation,
//   carence, plafonds, mode calcul, utilisation, interruption, rupture)
// Module : features/assistance_sociale/pages
// Charte : .tab / .formTitle / .bG / .bt_add / .tabItem / .tabElement / InputForm
// Pattern : barre d'action cSAL (Ajouter, Modifier, Enregistrer) par onglet
// ============================================================================

import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Spin, Button, Form, Space, Tabs, InputNumber, Select, Input, Divider, Modal } from 'antd';
import type { TabsProps, FormInstance } from 'antd';
import { Box, Typography } from '@mui/material';
import { ColumnsType } from 'antd/es/table';
import SweetAlert from 'sweetalert2';
import { useAuthUser } from '../../../Services/Auth/sessionAuth';
import Utilisateur from '../../../Models/Utilisateur';
import SearchModal from '../../../Components/SearchModal';
import ParamsPicklist from '../../../Types/ParamsPicklist';
import {
  useGetEligibilite,
  useSaveEligibilite,
  useGetCotisation,
  useSaveCotisation,
  useGetCarence,
  useSaveCarence,
  useDeleteCarence,
  useGetInterruption,
  useSaveInterruption,
  useGetRupture,
  useSaveRupture,
  useGetEvenementsByVersion,
  useCreateEvenement,
  useUpdateEvenement,
  useDeleteEvenement,
  useGetCatalogueEvenements,
  useGetRegleEvenement,
  useSaveRegleEvenement,
  useSavePlafond,
  useSaveUtilisation,
  useGetDocumentsRequisByVersion,
  useSaveDocumentRequis,
  useDeleteDocumentRequis,
} from '../hooks';
import SafeCarePageModal from '../components/SafeCarePageModal';
import {
  RegleEligibilite,
  RegleCotisation,
  RegleCarence,
  RegleInterruption,
  RegleRupture,
  EvenementCouvert,
  RegleEvenement,
  Plafond,
  RegleUtilisation,
  DocumentRequis,
  CatalogueEvenement,
} from '../types';

// ============================================================================
// État des boutons d'action (identique à ProduitAdmin)
// ============================================================================
interface ActionState {
  ajout: boolean;   // bouton Ajouter désactivé ?
  modif: boolean;   // bouton Modifier désactivé ?
  enreg: boolean;   // bouton Enregistrer désactivé ?
  annul: boolean;   // bouton Annuler désactivé ?
  editMode: boolean; // formulaire en édition ?
}

function defaultActionState(): ActionState {
  return { ajout: false, modif: true, enreg: true, annul: true, editMode: false };
}

function viewActionState(): ActionState {
  return { ajout: true, modif: false, enreg: true, annul: true, editMode: false };
}

// ============================================================================
// Barre d'action — utilise .bG (position absolute left 89%) comme ProduitAdmin
// ============================================================================
const ActionBar = ({
  state,
  onAjouter,
  onModifier,
  onEnregistrer,
  onAnnuler,
}: {
  state: ActionState;
  onAjouter: () => void;
  onModifier: () => void;
  onEnregistrer: () => void;
  onAnnuler: () => void;
}) => (
  <div className="bG" style={{ padding: 12 }}>
    <legend style={{ marginBottom: 12 }}>
      <Typography color="#116599">Actions</Typography>
    </legend>
    <Form.Item shouldUpdate className="bouttong" style={{ margin: 0 }}>
      <Space direction="vertical" size={10} style={{ width: '100%' }}>
        <Button size="large" className="bt_add" disabled={state.ajout} onClick={onAjouter} type="ghost" htmlType="button" block>Ajouter</Button>
        <Button size="large" className="bt_add" disabled={state.modif} onClick={onModifier} type="ghost" htmlType="button" block>Modifier</Button>
        <Button size="large" className="bt_add" disabled={state.enreg} onClick={onEnregistrer} type="ghost" htmlType="button" block>Enregistrer</Button>
        <Button size="large" className="bt_add" disabled={state.annul} onClick={onAnnuler} type="ghost" htmlType="button" block>Annuler</Button>
      </Space>
    </Form.Item>
  </div>
);

// ============================================================================
// Barre d'action regroupée — une seule barre (.bG) pour un onglet qui fusionne
// plusieurs interfaces : les boutons sont rangés par section.
// ============================================================================
interface GroupedAction { label: string; disabled?: boolean; onClick: () => void; }
interface ActionGroup { titre: string; actions: GroupedAction[]; }

const GroupedActionBar = ({ groups }: { groups: ActionGroup[] }) => (
  <div className="bG" style={{ padding: 12 }}>
    <legend style={{ marginBottom: 8 }}>
      <Typography color="#116599">Actions</Typography>
    </legend>
    <div className="bouttong">
      {groups.map((g, gi) => (
        <Box key={g.titre} sx={{ mt: gi === 0 ? 0 : 1.5 }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'var(--darkblue)', textTransform: 'uppercase', mb: 0.75 }}>
            {g.titre}
          </Typography>
          <Space direction="vertical" size={6} style={{ width: '100%' }}>
            {g.actions.map((a) => (
              <Button key={a.label} size="large" className="bt_add" disabled={a.disabled} onClick={a.onClick} type="ghost" htmlType="button" block>
                {a.label}
              </Button>
            ))}
          </Space>
        </Box>
      ))}
    </div>
  </div>
);

const actionsCrud = (state: ActionState, h: { ajouter: () => void; modifier: () => void; enregistrer: () => void; annuler: () => void }): GroupedAction[] => [
  { label: 'Ajouter', disabled: state.ajout, onClick: h.ajouter },
  { label: 'Modifier', disabled: state.modif, onClick: h.modifier },
  { label: 'Enregistrer', disabled: state.enreg, onClick: h.enregistrer },
  { label: 'Annuler', disabled: state.annul, onClick: h.annuler },
];

// ============================================================================
// Helper générique pour créer les handlers d'action
// ============================================================================
function makeHandlers(form: any, setState: (s: ActionState) => void, idVersion?: number) {
  const init = () => setState(defaultActionState());

  const ajouter = () => {
    form.resetFields();
    if (idVersion) form.setFieldValue('IdVersion', idVersion);
    setState({ ajout: true, modif: true, enreg: false, annul: false, editMode: true });
  };

  const modifier = () => {
    setState({ ajout: true, modif: true, enreg: false, annul: false, editMode: true });
  };

  const enregistrer = () => {
    form.submit();
  };

  const annuler = () => {
    form.resetFields();
    init();
  };

  return { init, ajouter, modifier, enregistrer, annuler };
}

// Picklists LIBASSURANCE pour les listes déroulantes du modal de règles
const searchModeCalcul: ParamsPicklist = { CodeAgence: '', Table: 'LIBASSURANCE', Colonne1: 'CODLIB', Colonne2: 'LIBELL', Colagence: '', colfiltre: 'RUBRIQ', valfiltre: 'SFC_MODECALCUL', Choix: 22 };
const searchTypePlafond: ParamsPicklist = { CodeAgence: '', Table: 'LIBASSURANCE', Colonne1: 'CODLIB', Colonne2: 'LIBELL', Colagence: '', colfiltre: 'RUBRIQ', valfiltre: 'SFC_TYPEPLAFOND', Choix: 22 };
const searchPeriodicite: ParamsPicklist = { CodeAgence: '', Table: 'LIBASSURANCE', Colonne1: 'CODLIB', Colonne2: 'LIBELL', Colagence: '', colfiltre: 'RUBRIQ', valfiltre: 'SFC_PERIODICITE', Choix: 22 };
const searchFrequence: ParamsPicklist = { CodeAgence: '', Table: 'LIB', Colonne1: 'CODLIB', Colonne2: 'LIBELL', Colagence: '', colfiltre: 'RUBRIQ', valfiltre: 'PERIOD', Choix: 22 };
const searchPlancp: ParamsPicklist = { CodeAgence: '', Table: 'PLANCP', Colonne1: 'IDPLAN', Colonne2: 'LIBELL', Colagence: '101', Choix: 22 };

interface ConfigurationVersionProps {
  open?: boolean;
  idVersion?: number;
  idProduit?: number;
  // Catégorie du produit : filtre le catalogue des événements proposables
  categorie?: string;
  // Statut de la version : les événements ne sont modifiables qu'en BROUILLON
  statutVersion?: string;
  onBack?: () => void;
}

const ConfigurationVersion = ({ open, idVersion: idVersionProp, idProduit: idProduitProp, categorie, statutVersion, onBack }: ConfigurationVersionProps) => {
  const navigate = useNavigate();
  const auth = useAuthUser();
  const user: Utilisateur = JSON.parse(JSON.stringify(auth()));

  const urlParams = new URLSearchParams(window.location.search);
  const idVersionParam = urlParams.get('idVersion');
  const idProduitParam = urlParams.get('idProduit');
  const idVersion = idVersionProp ?? (idVersionParam ? parseInt(idVersionParam) : undefined);
  const idProduit = idProduitProp ?? (idProduitParam ? parseInt(idProduitParam) : undefined);

  const [formElig] = Form.useForm();
  const [formCot] = Form.useForm();
  const [formCar] = Form.useForm();
  const [formInt] = Form.useForm();
  const [formRup] = Form.useForm();
  const [formDocReq] = Form.useForm();
  const [formDocReqRup] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('2');

  // États des barres d'action par onglet
  const [stateElig, setStateElig] = useState<ActionState>(defaultActionState());
  const [stateCot, setStateCot] = useState<ActionState>(defaultActionState());
  const [stateInt, setStateInt] = useState<ActionState>(defaultActionState());
  const [stateRup, setStateRup] = useState<ActionState>(defaultActionState());
  const [stateDocReq, setStateDocReq] = useState<ActionState>(defaultActionState());
  const [selectedDocReq, setSelectedDocReq] = useState<DocumentRequis | null>(null);
  const [stateDocReqRup, setStateDocReqRup] = useState<ActionState>(defaultActionState());
  const [selectedDocReqRup, setSelectedDocReqRup] = useState<DocumentRequis | null>(null);

  // Hooks pour charger les règles existantes
  const { data: eligibiliteRes } = useGetEligibilite(idVersion);
  const { data: cotisationRes } = useGetCotisation(idVersion);
  const { data: carenceRes } = useGetCarence(idVersion);
  const { data: interruptionRes } = useGetInterruption(idVersion);
  const { data: ruptureRes } = useGetRupture(idVersion);
  const { data: evenementsRes, isLoading: loadingEve } = useGetEvenementsByVersion(idVersion);
  const { data: catEveRes } = useGetCatalogueEvenements(categorie);
  const catalogueEve: CatalogueEvenement[] =
    catEveRes?.StatutCode === 200 ? catEveRes.Result?.Table1 ?? [] : [];
  const createEvenement = useCreateEvenement();
  const updateEvenement = useUpdateEvenement();
  const deleteEvenement = useDeleteEvenement();
  const evenementsModifiables = statutVersion !== 'PUBLIE';

  const saveEligibilite = useSaveEligibilite();
  const saveCotisation = useSaveCotisation();
  const saveCarence = useSaveCarence();
  const saveInterruption = useSaveInterruption();
  const saveRupture = useSaveRupture();
  const saveRegleEvenement = useSaveRegleEvenement();
  const savePlafond = useSavePlafond();
  const saveUtilisation = useSaveUtilisation();
  const saveDocumentRequis = useSaveDocumentRequis();
  const deleteDocumentRequis = useDeleteDocumentRequis();
  const { data: documentsRequisRes, refetch: refetchDocReq } = useGetDocumentsRequisByVersion(idVersion);


  const evenements: EvenementCouvert[] =
    evenementsRes?.StatutCode === 200 ? evenementsRes.Result?.Table1 ?? [] : [];

  // Verrou cotisation : dès qu'une souscription est active (ou suspendue) sur la
  // version, montant / fréquence / jour de prélèvement sont figés — des échéances
  // ont déjà été générées sur cette base (même traitement que les événements
  // verrouillés d'une version publiée). Compteur calculé côté SQL.
  const nbSouscriptionsActives = cotisationRes?.StatutCode === 200
    ? (cotisationRes.Result?.Table1?.[0]?.NbSouscriptionsActives ?? 0)
    : 0;
  const cotisationVerrouillee = nbSouscriptionsActives > 0;

  // Charger les valeurs dans les formulaires quand les données arrivent
  // (ou à chaque ré-ouverture / changement de version : le composant reste monté,
  // les instances de formulaire sont donc réutilisées et doivent être resynchronisées).
  const applyRegle = (res: any, form: FormInstance, setState: (s: ActionState) => void) => {
    if (open === false) return;
    form.resetFields();
    if (res?.StatutCode === 200 && res.Result?.Table1?.[0]) {
      form.setFieldsValue(res.Result.Table1[0]);
      setState(viewActionState());
    } else {
      setState(defaultActionState());
    }
  };

  useEffect(() => { applyRegle(eligibiliteRes, formElig, setStateElig); }, [eligibiliteRes, idVersion, open]);
  useEffect(() => { applyRegle(cotisationRes, formCot, setStateCot); }, [cotisationRes, idVersion, open]);
  useEffect(() => { applyRegle(interruptionRes, formInt, setStateInt); }, [interruptionRes, idVersion, open]);
  useEffect(() => { applyRegle(ruptureRes, formRup, setStateRup); }, [ruptureRes, idVersion, open]);

  // Revenir sur le premier onglet quand on ouvre la configuration d'une autre version
  useEffect(() => { setActiveTab('2'); }, [idVersion]);

  // ==================== SAUVEGARDES ====================
  const saveElig = (values: any) => {
    const regle: RegleEligibilite = { ...values, IdVersion: idVersion! };
    setLoading(true);
    saveEligibilite.mutate(regle, {
      onSuccess: (res) => {
        setLoading(false);
        if (res?.StatutCode === 200) {
          SweetAlert.fire({ title: 'Succès', text: 'Règles d\'éligibilité enregistrées', icon: 'success', timer: 1500 });
          setStateElig(defaultActionState());
        } else {
          SweetAlert.fire({ title: 'Attention', text: res?.Message ?? 'Erreur', icon: 'warning' });
        }
      },
    });
  };

  const saveCot = (values: any) => {
    if (cotisationVerrouillee) {
      SweetAlert.fire({
        title: 'Cotisation verrouillée',
        text: 'Des souscriptions sont actives sur cette version : le montant, la fréquence et le jour de prélèvement ne peuvent plus être modifiés.',
        icon: 'warning',
      });
      return;
    }
    const regle: RegleCotisation = { ...values, IdVersion: idVersion! };
    setLoading(true);
    saveCotisation.mutate(regle, {
      onSuccess: (res) => {
        if (res?.StatutCode === 200) {
          setLoading(false);
          SweetAlert.fire({ title: 'Succès', text: 'Cotisation enregistrée', icon: 'success', timer: 1500 });
          setStateCot(defaultActionState());
        } else {
          setLoading(false);
          SweetAlert.fire({ title: 'Attention', text: res?.Message ?? 'Erreur', icon: 'warning' });
        }
      },
      onError: () => setLoading(false),
    });
  };

  const saveInt = (values: any) => {
    const regle: RegleInterruption = { ...values, IdVersion: idVersion! };
    setLoading(true);
    saveInterruption.mutate(regle, {
      onSuccess: (res) => {
        setLoading(false);
        if (res?.StatutCode === 200) {
          SweetAlert.fire({ title: 'Succès', text: 'Règles d\'interruption enregistrées', icon: 'success', timer: 1500 });
          setStateInt(defaultActionState());
        } else {
          SweetAlert.fire({ title: 'Attention', text: res?.Message ?? 'Erreur', icon: 'warning' });
        }
      },
    });
  };

  const saveRup = (values: any) => {
    const regle: RegleRupture = { ...values, IdVersion: idVersion! };
    setLoading(true);
    saveRupture.mutate(regle, {
      onSuccess: (res) => {
        setLoading(false);
        if (res?.StatutCode === 200) {
          SweetAlert.fire({ title: 'Succès', text: 'Règles de rupture enregistrées', icon: 'success', timer: 1500 });
          setStateRup(defaultActionState());
        } else {
          SweetAlert.fire({ title: 'Attention', text: res?.Message ?? 'Erreur', icon: 'warning' });
        }
      },
    });
  };

  // Handlers pour chaque onglet
  const hElig = makeHandlers(formElig, setStateElig, idVersion);
  const hCot = makeHandlers(formCot, setStateCot, idVersion);
  const hInt = makeHandlers(formInt, setStateInt, idVersion);
  const hRup = makeHandlers(formRup, setStateRup, idVersion);

  // ==================== DOCUMENTS REQUIS (données + handlers) ====================
  // Le contexte distingue les documents exigés à l'indemnisation de ceux
  // exigés à la suspension/résiliation (rupture).
  const documentsRequis: DocumentRequis[] =
    documentsRequisRes?.StatutCode === 200 ? documentsRequisRes.Result?.Table1 ?? [] : [];
  const documentsRequisIndem = documentsRequis.filter((d) => (d.Contexte ?? 'INDEMNISATION') === 'INDEMNISATION');
  const documentsRequisRup = documentsRequis.filter((d) => d.Contexte === 'RUPTURE');

  const makeDocReqOps = (
    formX: any,
    setStateX: (s: ActionState) => void,
    selectedX: DocumentRequis | null,
    setSelectedX: (d: DocumentRequis | null) => void,
    list: DocumentRequis[],
    contexte: 'INDEMNISATION' | 'RUPTURE',
  ) => {
    const save = (values: any) => {
      if (!idVersion) return;
      setLoading(true);
      const payload: DocumentRequis = {
        IdDocumentRequis: values.IdDocumentRequis || undefined,
        IdVersion: idVersion,
        IdEvenement: contexte === 'INDEMNISATION' ? (values.IdEvenement || undefined) : undefined,
        LibelleDocument: values.LibelleDocument,
        Description: values.Description,
        Obligatoire: values.Obligatoire ?? true,
        Ordre: values.Ordre ?? 0,
        Contexte: contexte,
        UtiSai: user?.coduti ?? 'ADMIN',
      };
      saveDocumentRequis.mutate(payload, {
        onSuccess: (res: any) => {
          setLoading(false);
          if (res?.StatutCode === 200) {
            SweetAlert.fire({ title: 'Succès', text: 'Document requis enregistré', icon: 'success', timer: 1500 });
            formX.resetFields();
            setSelectedX(null);
            setStateX(defaultActionState());
            refetchDocReq();
          } else {
            SweetAlert.fire({ title: 'Attention', text: res?.Message ?? 'Erreur', icon: 'warning' });
          }
        },
        onError: (err: any) => {
          setLoading(false);
          SweetAlert.fire({ title: 'Erreur', text: err?.message ?? 'Erreur', icon: 'error' });
        },
      });
    };

    const onSelect = (record: DocumentRequis) => {
      setSelectedX(record);
      formX.setFieldsValue(record);
      setStateX(viewActionState());
    };

    const onDelete = () => {
      if (!selectedX?.IdDocumentRequis) {
        SweetAlert.fire({ title: 'Attention', text: 'Sélectionnez un document à supprimer', icon: 'warning' });
        return;
      }
      SweetAlert.fire({
        title: 'Confirmer',
        text: `Supprimer le document "${selectedX.LibelleDocument}" ?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Oui',
        cancelButtonText: 'Non',
      }).then((res) => {
        if (res.isConfirmed) {
          setLoading(true);
          deleteDocumentRequis.mutate(selectedX.IdDocumentRequis!, {
            onSuccess: (r: any) => {
              setLoading(false);
              if (r?.StatutCode === 200) {
                SweetAlert.fire({ title: 'Succès', text: 'Document supprimé', icon: 'success', timer: 1500 });
                formX.resetFields();
                setSelectedX(null);
                setStateX(defaultActionState());
                refetchDocReq();
              } else {
                SweetAlert.fire({ title: 'Attention', text: r?.Message ?? 'Erreur', icon: 'warning' });
              }
            },
            onError: (err: any) => {
              setLoading(false);
              SweetAlert.fire({ title: 'Erreur', text: err?.message ?? 'Erreur', icon: 'error' });
            },
          });
        }
      });
    };

    const h = {
      ajouter: () => {
        formX.resetFields();
        if (idVersion) formX.setFieldValue('IdVersion', idVersion);
        formX.setFieldValue('Obligatoire', true);
        formX.setFieldValue('Ordre', list.length + 1);
        setSelectedX(null);
        setStateX({ ajout: true, modif: true, enreg: false, annul: false, editMode: true });
      },
      modifier: () => {
        if (!selectedX) {
          SweetAlert.fire({ title: 'Attention', text: 'Sélectionnez un document à modifier', icon: 'warning' });
          return;
        }
        setStateX({ ajout: true, modif: true, enreg: false, annul: false, editMode: true });
      },
      enregistrer: () => formX.submit(),
      annuler: () => {
        formX.resetFields();
        setSelectedX(null);
        setStateX(defaultActionState());
      },
    };

    return { save, onSelect, onDelete, h };
  };

  const docReqIndem = makeDocReqOps(formDocReq, setStateDocReq, selectedDocReq, setSelectedDocReq, documentsRequisIndem, 'INDEMNISATION');
  const docReqRup = makeDocReqOps(formDocReqRup, setStateDocReqRup, selectedDocReqRup, setSelectedDocReqRup, documentsRequisRup, 'RUPTURE');

  // ==================== ONGLET COTISATION ====================
  const CotisationTab = () => {
    return (
      <Form
        form={formCot}
        layout="vertical"
        onFinish={saveCot}
        disabled={!stateCot.editMode || cotisationVerrouillee}
      >
        <ActionBar
          state={cotisationVerrouillee ? { ...stateCot, ajout: true, modif: true, enreg: true } : stateCot}
          onAjouter={hCot.ajouter}
          onModifier={hCot.modifier}
          onEnregistrer={hCot.enregistrer}
          onAnnuler={hCot.annuler}
        />
        <div className="clientP">
          <div className="tabItem">
            <div>

              {/* Formulaire COTISATION en dessous */}
              <Box fontStyle="inherit" width="85%" sx={{ p: 3, mb: 3 }}>
                <legend style={{ marginBottom: 16 }}>Cotisation</legend>
                {cotisationVerrouillee && (
                  <Typography variant="caption" sx={{ color: '#ed6c02', display: 'block', mb: 1 }}>
                    {nbSouscriptionsActives} souscription(s) active(s) sur cette version : le montant, la fréquence et le jour de prélèvement sont verrouillés. Créez une nouvelle version pour les modifier.
                  </Typography>
                )}
                <Form.Item name="IdRegleCotisation" hidden><input type="hidden" /></Form.Item>
                <Form.Item name="IdVersion" hidden><input type="hidden" /></Form.Item>
                <Space wrap size="middle">
                  <Form.Item name="Montant" label="Montant cotisation (FCFA)" rules={[{ required: true }]}>
                    <InputNumber min={0} style={{ width: 200 }} />
                  </Form.Item>
                  <SearchModal
                    Titre="Fréquence"
                    label="Fréquence"
                    name="CodeFrequence"
                    Parametre={searchFrequence}
                    form={formCot}
                    defaultCode={String(formCot.getFieldValue('CodeFrequence') ?? '')}
                    defaultLibelle={String(formCot.getFieldValue('lib_CodeFrequence') ?? '')}
                    width={12}
                    Taille1={167}
                    Taille2={320}
                    notRequired
                  />
                </Space>
              </Box>
              {/* Ventilation comptable : paramétrée sur le code opération de la version */}
              <Box fontStyle="inherit" width="85%" sx={{ p: 3, mb: 3 }}>
                <legend style={{ marginBottom: 8 }}>Ventilation de la cotisation</legend>
                <Typography variant="body2" color="textSecondary">
                  Lors du prélèvement, le compte du client est débité du montant total et les comptes de
                  contrepartie paramétrés pour le code opération de cotisation de la version (agence de la
                  version) sont crédités selon leur quote-part.
                </Typography>
              </Box>

            </div>
          </div>
        </div>
      </Form>
    );
  };

  // ==================== ONGLET ÉLIGIBILITÉ & CARENCE ====================
  const EligibiliteCarenceTab = () => {
    // On mémorise la clé de la ligne : la ligne affichée est relue après chaque
    // enregistrement / suppression (durée et IdRegleCarence toujours à jour).
    const [selectedCarKey, setSelectedCarKey] = useState<string | null>(null);
    const setSelectedCar = (r: any | null) => setSelectedCarKey(r?._key ?? null);
    const [modalCarOpen, setModalCarOpen] = useState(false);
    const delCarence = useDeleteCarence();

    // Lignes de la table : la carence générale en premier (toujours affichée),
    // puis une ligne par événement couvert — spécifique ou héritée de la générale.
    const carences: RegleCarence[] =
      carenceRes?.StatutCode === 200 ? carenceRes.Result?.Table1 ?? [] : [];

    const generalDuree = carences.find((c) => c.IdEvenement == null)?.DureeJours;

    const carenceRows = useMemo(() => {
      const rows: any[] = [];
      const general = carences.find((c) => c.IdEvenement == null);
      rows.push({
        ...(general ?? { IdVersion: idVersion ?? 0 }),
        _key: 'gen',
        _isGeneral: true,
        _inherited: !general,
        lib_CodeEvenement: undefined,
        lib_Evenement: 'Tous les événements (générale)',
      });
      evenements.forEach((e) => {
        const c = carences.find((x) => x.IdEvenement === e.IdEvenement);
        rows.push({
          IdRegleCarence: c?.IdRegleCarence,
          IdVersion: idVersion ?? 0,
          IdEvenement: e.IdEvenement,
          DureeJours: c?.DureeJours,
          lib_CodeEvenement: e.Code,
          lib_Evenement: e.Libelle,
          _key: `eve-${e.IdEvenement}`,
          _isGeneral: false,
          _inherited: !c,
        });
      });
      return rows;
    }, [carenceRes, evenements, idVersion]);

    const selectedCar = carenceRows.find((r) => r._key === selectedCarKey) ?? null;

    const columnsCar: ColumnsType<any> = [
      {
        title: 'Événement',
        render: (_, r) => (
          <span>
            {r.lib_CodeEvenement ? <b>{r.lib_CodeEvenement} — </b> : null}{r.lib_Evenement}
          </span>
        ),
      },
      {
        title: 'Durée (jours)',
        width: 150,
        align: 'center',
        render: (_, r) =>
          r.DureeJours != null ? <b>{r.DureeJours}</b>
            : r._isGeneral ? <span style={{ color: '#8c8c8c' }}>Non définie</span>
            : <span style={{ color: '#8c8c8c' }}>Générale : {generalDuree ?? '—'}</span>,
      },
      {
        title: 'Type',
        width: 120,
        align: 'center',
        render: (_, r) => (r._isGeneral ? 'Générale' : r._inherited ? 'Héritée' : 'Spécifique'),
      },
    ];

    const openCar = (record: any) => {
      setSelectedCar(record);
      formCar.setFieldsValue({ IdRegleCarence: record.IdRegleCarence, DureeJours: record.DureeJours });
      setModalCarOpen(true);
    };

    const closeCar = () => {
      setModalCarOpen(false);
      formCar.resetFields();
    };

    const saveCarModal = (values: any) => {
      const regle: RegleCarence = {
        IdVersion: idVersion!,
        IdEvenement: selectedCar?._isGeneral ? undefined : selectedCar?.IdEvenement,
        DureeJours: values.DureeJours,
      };
      setLoading(true);
      saveCarence.mutate(regle, {
        onSuccess: (res) => {
          setLoading(false);
          if (res?.StatutCode === 200) {
            SweetAlert.fire({ title: 'Succès', text: 'Carence enregistrée', icon: 'success', timer: 1500 });
            closeCar();
          } else {
            SweetAlert.fire({ title: 'Attention', text: res?.Message ?? 'Erreur', icon: 'warning' });
          }
        },
        onError: () => setLoading(false),
      });
    };

    const deleteCar = async () => {
      if (!selectedCar?.IdRegleCarence) return;
      const conf = await SweetAlert.fire({
        title: 'Suppression',
        text: `Supprimer la carence « ${selectedCar.lib_Evenement} » ?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#116599',
        cancelButtonColor: 'darkred',
        confirmButtonText: 'Oui',
        cancelButtonText: 'Non',
      });
      if (!conf.isConfirmed) return;
      setLoading(true);
      delCarence.mutate(selectedCar.IdRegleCarence, {
        onSuccess: (res) => {
          setLoading(false);
          if (res?.StatutCode === 200) {
            SweetAlert.fire({ title: 'Succès', text: res.Message, icon: 'success', timer: 1500 });
            closeCar();
            setSelectedCar(null);
          } else {
            SweetAlert.fire({ title: 'Attention', text: res?.Message ?? 'Erreur', icon: 'warning' });
          }
        },
        onError: () => setLoading(false),
      });
    };

    return (
      <div>
        <GroupedActionBar
          groups={[
            { titre: 'Éligibilité', actions: actionsCrud(stateElig, hElig) },
            {
              titre: 'Carence',
              actions: [
                { label: 'Configurer', disabled: !selectedCar, onClick: () => selectedCar && openCar(selectedCar) },
                { label: 'Supprimer', disabled: selectedCar?.IdRegleCarence == null, onClick: deleteCar },
                { label: 'Désélectionner', disabled: !selectedCar, onClick: () => setSelectedCar(null) },
              ],
            },
          ]}
        />

        <Form form={formElig} layout="vertical" onFinish={saveElig} disabled={!stateElig.editMode}>
          <Box fontStyle="inherit" width="85%" sx={{ p: 3, mb: 3 }}>
            <legend style={{ marginBottom: 16 }}>Conditions d'éligibilité</legend>
            <Form.Item name="IdRegleEligibilite" hidden><input type="hidden" /></Form.Item>
            <Form.Item name="IdVersion" hidden><input type="hidden" /></Form.Item>
            <Space wrap size="middle">
              <Form.Item name="AgeMin" label="Âge minimum">
                <InputNumber min={0} max={120} />
              </Form.Item>
              <Form.Item name="AgeMax" label="Âge maximum">
                <InputNumber min={0} max={120} />
              </Form.Item>
              <Form.Item name="TypCliAutorise" label="Type client autorisé">
                <Input placeholder="Ex: P,M" />
              </Form.Item>
              <Form.Item name="CatCliAutorise" label="Catégorie client">
                <Input placeholder="Ex: E,P" />
              </Form.Item>
              <Form.Item name="CdetatAutorise" label="État civil autorisé">
                <Input placeholder="Ex: C,M,D,V" />
              </Form.Item>
              <Form.Item name="CdResAutorise" label="Code résidence">
                <Input placeholder="Ex: 01,02" />
              </Form.Item>
              <Form.Item name="SitfamAutorise" label="Situation familiale">
                <Input placeholder="Ex: C,M" />
              </Form.Item>
            </Space>
          </Box>
        </Form>

        <Box fontStyle="inherit" width="85%" sx={{ p: 3, mb: 3 }}>
          <legend style={{ marginBottom: 8 }}>Période de carence par événement</legend>
          <Typography variant="caption" color="textSecondary">
            Sélectionnez une ligne puis « Configurer » (ou double-cliquez). Un événement sans carence spécifique hérite de la carence générale.
          </Typography>
          <Spin spinning={loadingEve}>
            <Table
              size="small"
              style={{ width: '100%', marginTop: 8 }}
              rowKey={(r) => r._key}
              columns={columnsCar}
              dataSource={carenceRows}
              scroll={{ y: 320 }}
              pagination={false}
              rowSelection={{
                type: 'radio',
                selectedRowKeys: selectedCar ? [selectedCar._key] : [],
                onChange: (_keys, rows) => setSelectedCar(rows[0] ?? null),
              }}
              onRow={(record) => ({
                onClick: () => setSelectedCar(record),
                onDoubleClick: () => openCar(record),
                style: { cursor: 'pointer' },
              })}
            />
          </Spin>
        </Box>

        {/* Modale de configuration — même pattern que la modale des règles d'événement,
            zIndex supérieur à SafeCarePageModal (2000). */}
        <Modal
          title={selectedCar ? `Carence : ${selectedCar.lib_CodeEvenement ? `${selectedCar.lib_CodeEvenement} - ` : ''}${selectedCar.lib_Evenement}` : ''}
          open={modalCarOpen}
          onCancel={closeCar}
          zIndex={2100}
          className="safeCareModal"
          width={520}
          footer={
            <div style={{ textAlign: 'right' }}>
              {selectedCar?.IdRegleCarence != null && (
                <Button danger size="large" onClick={deleteCar} loading={loading} style={{ minWidth: 120, marginRight: 8 }}>
                  Supprimer
                </Button>
              )}
              <Button type="primary" size="large" onClick={() => formCar.submit()} loading={loading} style={{ minWidth: 120, marginRight: 8 }}>
                Enregistrer
              </Button>
              <Button size="large" onClick={closeCar} style={{ minWidth: 120 }}>Fermer</Button>
            </div>
          }
        >
          <Spin spinning={loading}>
            <Form form={formCar} layout="vertical" onFinish={saveCarModal}>
              <Form.Item name="IdRegleCarence" hidden><input type="hidden" /></Form.Item>
              <Form.Item name="DureeJours" label="Durée de carence (jours)" rules={[{ required: true, message: 'Durée obligatoire' }]}>
                <InputNumber min={0} max={365} style={{ width: 200 }} />
              </Form.Item>
              <Typography variant="caption" color="textSecondary">
                {selectedCar?._isGeneral
                  ? "Carence appliquée à tous les événements qui n'ont pas de carence spécifique."
                  : `Événement : ${selectedCar?.lib_CodeEvenement ?? ''} ${selectedCar?.lib_Evenement ?? ''}`}
              </Typography>
            </Form>
          </Spin>
        </Modal>
      </div>
    );
  };

  // ==================== ONGLET ÉVÉNEMENTS + RÈGLES ====================

  const EvenementRegleTab = () => {
    const [selectedEve, setSelectedEve] = useState<EvenementCouvert | null>(null);
    const [modalOpen, setModalOpen] = useState(false);
    // Un seul formulaire regroupe les 3 sections (mode de calcul, plafond,
    // utilisation) pour permettre un enregistrement centralisé en un clic.
    const [formEveTout] = Form.useForm();
    const [eveEditMode, setEveEditMode] = useState(false);
    const [hasExistingRule, setHasExistingRule] = useState(false);

    const { data: regleEveRes } = useGetRegleEvenement(selectedEve?.IdEvenement);

    useEffect(() => {
      const dataRows = regleEveRes?.Result?.Table1 ?? regleEveRes?.Result?.Table;
      if (regleEveRes?.StatutCode === 200 && dataRows?.[0]) {
        const r = dataRows[0];
        setHasExistingRule(true);
        setEveEditMode(false);
        formEveTout.setFieldsValue({
          IdRegleEvenement: r.IdRegleEvenement,
          ModeCalcul: r.ModeCalcul,
          Montant: r.Montant,
          Pourcentage: r.Pourcentage,
          MontantPlafondCalcul: r.MontantPlafondCalcul ?? r.MontantPlafond,
          TypePlafond: r.TypePlafond,
          MontantPlafond: r.MontantPlafond_2 !== undefined ? r.MontantPlafond_2 : r.MontantPlafond,
          NbMaxUtilisations: r.NbMaxUtilisations,
          Periodicite: r.Periodicite,
        });
      } else {
        setHasExistingRule(false);
        setEveEditMode(true);
        formEveTout.resetFields();
      }
    }, [regleEveRes]);

    // ---------- Gestion des événements couverts (ajout / modification / suppression) ----------
    const [formEveDef] = Form.useForm();
    const [eveDefOpen, setEveDefOpen] = useState(false);
    const [eveEnEdition, setEveEnEdition] = useState<EvenementCouvert | null>(null);

    const codesDejaAjoutes = new Set(evenements.map((e) => e.Code));

    const ouvrirAjoutEve = () => {
      setEveEnEdition(null);
      formEveDef.resetFields();
      formEveDef.setFieldValue('Ordre', evenements.length + 1);
      setEveDefOpen(true);
    };

    const ouvrirModifEve = (record: EvenementCouvert) => {
      setEveEnEdition(record);
      formEveDef.setFieldsValue(record);
      setEveDefOpen(true);
    };

    const choisirCatalogue = (code: string) => {
      const c = catalogueEve.find((x) => x.CodeEvenement === code);
      formEveDef.setFieldsValue({ Libelle: c?.LibelleEvenement, Description: c?.DescEvenement });
    };

    const enregistrerEve = async (values: any) => {
      const payload: EvenementCouvert = {
        ...values,
        IdVersion: idVersion!,
        IdEvenement: eveEnEdition?.IdEvenement,
      };
      setLoading(true);
      try {
        const res = await (eveEnEdition ? updateEvenement : createEvenement).mutateAsync(payload);
        setLoading(false);
        if (res?.StatutCode === 200) {
          SweetAlert.fire({ title: 'Succès', text: res.Message, icon: 'success', timer: 1500 });
          setEveDefOpen(false);
          setEveEnEdition(null);
        } else {
          SweetAlert.fire({ title: 'Attention', text: res?.Message ?? 'Erreur', icon: 'warning' });
        }
      } catch (err: any) {
        setLoading(false);
        SweetAlert.fire({ title: 'Erreur', text: err?.message ?? 'Erreur', icon: 'error' });
      }
    };

    const supprimerEve = (record: EvenementCouvert) => {
      SweetAlert.fire({
        title: 'Suppression',
        text: `Supprimer l'événement ${record.Code} — ${record.Libelle} et ses règles ?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#116599',
        cancelButtonColor: 'darkred',
        confirmButtonText: 'Oui',
        cancelButtonText: 'Non',
      }).then(async (r) => {
        if (!r.isConfirmed) return;
        setLoading(true);
        try {
          const res = await deleteEvenement.mutateAsync(record.IdEvenement!);
          setLoading(false);
          if (res?.StatutCode === 200) setEveCourant(null);
          SweetAlert.fire({
            title: res?.StatutCode === 200 ? 'Succès' : 'Attention',
            text: res?.Message ?? '',
            icon: res?.StatutCode === 200 ? 'success' : 'warning',
            timer: res?.StatutCode === 200 ? 1500 : undefined,
          });
        } catch (err: any) {
          setLoading(false);
          SweetAlert.fire({ title: 'Erreur', text: err?.message ?? 'Erreur', icon: 'error' });
        }
      });
    };

    const columnsEve: ColumnsType<EvenementCouvert> = [
      { title: 'Code', dataIndex: 'Code', width: 100 },
      { title: 'Libellé', dataIndex: 'Libelle', width: 200 },
      { title: 'Description', dataIndex: 'Description', ellipsis: true },
      { title: 'Ordre', dataIndex: 'Ordre', width: 80 },
    ];

    // Événement sélectionné dans le tableau (cible des actions de la barre)
    const [eveCourant, setEveCourant] = useState<EvenementCouvert | null>(null);
    const exigerSelection = (action: (e: EvenementCouvert) => void) => () => {
      if (!eveCourant) {
        SweetAlert.fire({ title: 'Attention', text: 'Sélectionnez un événement dans le tableau.', icon: 'warning' });
        return;
      }
      action(eveCourant);
    };

    const onSelectEve = (record: EvenementCouvert) => {
      setSelectedEve(record);
      setEveEditMode(false);
      setHasExistingRule(false);
      setModalOpen(true);
    };

    const closeModal = () => {
      setModalOpen(false);
      setSelectedEve(null);
      setEveEditMode(false);
      setHasExistingRule(false);
    };

    const handleModifierClick = () => {
      setEveEditMode(true);
    };

    // Enregistrement centralisé : mode de calcul -> plafond -> utilisation,
    // à la suite, avec un seul message de confirmation à la fin.
    const saveTout = async (values: any) => {
      setLoading(true);
      try {
        const regle: RegleEvenement = {
          IdRegleEvenement: values.IdRegleEvenement,
          IdEvenement: selectedEve!.IdEvenement!,
          ModeCalcul: values.ModeCalcul,
          Montant: values.Montant,
          Pourcentage: values.Pourcentage,
          MontantPlafond: values.MontantPlafondCalcul,
        } as RegleEvenement;
        const regleRes = await saveRegleEvenement.mutateAsync(regle);

        if (regleRes?.StatutCode !== 200) {
          setLoading(false);
          SweetAlert.fire({ title: 'Attention', text: regleRes?.Message ?? 'Erreur lors de l\'enregistrement du mode de calcul', icon: 'warning' });
          return;
        }

        const idRegleEvenement =
          regleRes?.Result?.Table1?.[0]?.IdRegleEvenement ??
          regleEveRes?.Result?.Table1?.[0]?.IdRegleEvenement ??
          regleEveRes?.Result?.Table?.[0]?.IdRegleEvenement ??
          0;

        const plafond: Plafond = {
          TypePlafond: values.TypePlafond,
          MontantPlafond: values.MontantPlafond,
          IdRegleEvenement: idRegleEvenement,
        } as Plafond;
        const plafondRes = await savePlafond.mutateAsync(plafond);

        if (plafondRes?.StatutCode !== 200) {
          setLoading(false);
          SweetAlert.fire({ title: 'Attention', text: plafondRes?.Message ?? 'Erreur lors de l\'enregistrement du plafond', icon: 'warning' });
          return;
        }

        const util: RegleUtilisation = {
          NbMaxUtilisations: values.NbMaxUtilisations,
          Periodicite: values.Periodicite,
          IdRegleEvenement: idRegleEvenement,
        } as RegleUtilisation;
        const utilRes = await saveUtilisation.mutateAsync(util);

        setLoading(false);
        if (utilRes?.StatutCode === 200) {
          SweetAlert.fire({ title: 'Succès', text: 'Règles enregistrées pour cet événement', icon: 'success', timer: 1500 });
        } else {
          SweetAlert.fire({ title: 'Attention', text: utilRes?.Message ?? 'Erreur lors de l\'enregistrement de la règle d\'utilisation', icon: 'warning' });
        }
      } catch (err: any) {
        setLoading(false);
        SweetAlert.fire({ title: 'Attention', text: err?.Message ?? 'Une erreur est survenue lors de l\'enregistrement', icon: 'warning' });
      }
    };

    return (
      <div>
        {/* Barre d'action (charte cSAL, identique aux autres onglets) */}
        <div className="bG" style={{ padding: 12 }}>
          <legend style={{ marginBottom: 12 }}>
            <Typography color="#116599">Actions</Typography>
          </legend>
          <div className="bouttong">
            <Space direction="vertical" size={10} style={{ width: '100%' }}>
              <Button size="large" className="bt_add" type="ghost" htmlType="button" block disabled={!evenementsModifiables || !categorie} onClick={ouvrirAjoutEve}>Ajouter</Button>
              <Button size="large" className="bt_add" type="ghost" htmlType="button" block disabled={!evenementsModifiables || !eveCourant} onClick={exigerSelection(ouvrirModifEve)}>Modifier</Button>
              <Button size="large" className="bt_add" type="ghost" htmlType="button" block disabled={!eveCourant} onClick={exigerSelection(onSelectEve)}>Règles</Button>
              <Button size="large" className="bt_add" type="ghost" htmlType="button" block disabled={!evenementsModifiables || !eveCourant} onClick={exigerSelection(supprimerEve)}>Supprimer</Button>
              <Button size="large" className="bt_add" type="ghost" htmlType="button" block disabled={!eveCourant} onClick={() => setEveCourant(null)}>Annuler</Button>
            </Space>
          </div>
        </div>

        <Box fontStyle="inherit" sx={{ p: 3, mb: 3 }}>
          <legend style={{ marginBottom: 8 }}>Événements couverts de la version</legend>
          <Typography variant="caption" color="textSecondary" display="block">
            1. « Ajouter » un événement du catalogue · 2. Sélectionnez-le dans le tableau puis « Règles » pour définir montant, plafonds et utilisation (double-clic : ouverture directe des règles).
          </Typography>
          {!evenementsModifiables && (
            <Typography variant="caption" sx={{ color: '#ed6c02', display: 'block', mt: 1 }}>
              Version publiée : la liste des événements est verrouillée. Créez une nouvelle version pour la modifier.
            </Typography>
          )}
          {evenementsModifiables && !categorie && (
            <Typography variant="caption" sx={{ color: '#d32f2f', display: 'block', mt: 1 }}>
              Le produit n'a pas de catégorie : aucun événement du catalogue ne peut être proposé.
            </Typography>
          )}
          {evenementsModifiables && categorie && evenements.length === 0 && !loadingEve && (
            <Typography variant="caption" sx={{ color: '#d32f2f', display: 'block', mt: 1 }}>
              Aucun événement couvert : ajoutez-en au moins un, la version ne peut pas être publiée sans événement.
            </Typography>
          )}
          <Spin spinning={loadingEve}>
            <Table
              size="small"
              style={{ width: '100%', marginTop: 8 }}
              rowKey="IdEvenement"
              columns={columnsEve}
              dataSource={evenements}
              scroll={{ y: 300 }}
              pagination={false}
              rowSelection={{
                type: 'radio',
                selectedRowKeys: eveCourant?.IdEvenement ? [eveCourant.IdEvenement] : [],
                onChange: (_keys, rows) => setEveCourant((rows[0] as EvenementCouvert) ?? null),
              }}
              onRow={(record) => ({
                onClick: () => setEveCourant(record),
                onDoubleClick: () => { setEveCourant(record); onSelectEve(record); },
                style: { cursor: 'pointer' },
              })}
            />
          </Spin>
        </Box>

        {/* Formulaire de règles affiché dans une Modal pour ne jamais
            se superposer au tableau ou aux titres.
            zIndex supérieur à celui de SafeCarePageModal (2000) pour
            apparaître au-dessus de la boîte de dialogue de configuration. */}
        <Modal
          title={selectedEve ? `Règles pour : ${selectedEve.Code} - ${selectedEve.Libelle}` : ''}
          open={modalOpen}
          onCancel={closeModal}
          zIndex={2100}
          className="safeCareModal"
          footer={
            <div style={{ textAlign: 'right' }}>
              {hasExistingRule && !eveEditMode ? (
                <Button type="primary" size="large" onClick={handleModifierClick} loading={loading} style={{ minWidth: 120, marginRight: 8 }}>
                  Modifier
                </Button>
              ) : (
                <Button type="primary" size="large" onClick={() => formEveTout.submit()} loading={loading} style={{ minWidth: 120, marginRight: 8 }}>
                  Enregistrer
                </Button>
              )}
              <Button size="large" onClick={closeModal} style={{ minWidth: 120 }}>
                Fermer
              </Button>
            </div>
          }
          width={800}
          destroyOnClose
        >
          {selectedEve && (
            <Form
              form={formEveTout}
              layout="vertical"
              onFinish={saveTout}
              disabled={!eveEditMode}
              onValuesChange={(changed) => {
                if ('ModeCalcul' in changed) {
                  const mode = changed.ModeCalcul;
                  if (mode === 'FIXE') {
                    formEveTout.resetFields(['Pourcentage', 'MontantPlafondCalcul']);
                  } else if (mode === 'POURCENTAGE') {
                    formEveTout.resetFields(['Montant', 'MontantPlafondCalcul']);
                  } else if (mode === 'POURCENTAGE_PLAFONNE') {
                    formEveTout.resetFields(['Montant']);
                  }
                }
              }}
            >
              <Form.Item name="IdRegleEvenement" hidden><input type="hidden" /></Form.Item>

              {/* Mode de calcul du montant */}
              <Typography variant="subtitle1" sx={{ mb: 1 }}>Montant de l'assistance</Typography>
              <Space wrap size="middle">
                <SearchModal
                  Titre="Mode de calcul"
                  label="Mode de calcul"
                  name="ModeCalcul"
                  Parametre={searchModeCalcul}
                  form={formEveTout}
                  defaultCode={String(formEveTout.getFieldValue('ModeCalcul') ?? '')}
                  defaultLibelle={String(formEveTout.getFieldValue('lib_ModeCalcul') ?? '')}
                  width={12}
                  Taille1={167}
                  Taille2={320}
                />
                <Form.Item noStyle shouldUpdate={(prev, cur) => prev.ModeCalcul !== cur.ModeCalcul}>
                  {(form) => {
                    const mode = form.getFieldValue('ModeCalcul');
                    return (
                      <>
                        {mode === 'FIXE' && (
                          <Form.Item name="Montant" label="Montant fixe (FCFA)">
                            <InputNumber min={0} style={{ width: 200 }} />
                          </Form.Item>
                        )}
                        {(mode === 'POURCENTAGE' || mode === 'POURCENTAGE_PLAFONNE') && (
                          <Form.Item name="Pourcentage" label="Pourcentage (%)">
                            <InputNumber min={0} max={100} style={{ width: 200 }} />
                          </Form.Item>
                        )}
                        {mode === 'POURCENTAGE_PLAFONNE' && (
                          <Form.Item name="MontantPlafondCalcul" label="Montant plafond (FCFA)">
                            <InputNumber min={0} style={{ width: 200 }} />
                          </Form.Item>
                        )}
                      </>
                    );
                  }}
                </Form.Item>
              </Space>

              <Divider />

              {/* Plafonds */}
              <Typography variant="subtitle1" sx={{ mb: 1 }}>Plafonds</Typography>
              <Space wrap size="middle">
                <SearchModal
                  Titre="Type de plafond"
                  label="Type de plafond"
                  name="TypePlafond"
                  Parametre={searchTypePlafond}
                  form={formEveTout}
                  defaultCode={String(formEveTout.getFieldValue('TypePlafond') ?? '')}
                  defaultLibelle={String(formEveTout.getFieldValue('lib_TypePlafond') ?? '')}
                  width={12}
                  Taille1={167}
                  Taille2={320}
                />
                <Form.Item name="MontantPlafond" label="Montant plafond (FCFA)" rules={[{ required: true }]}>
                  <InputNumber min={0} style={{ width: 200 }} />
                </Form.Item>
              </Space>

              <Divider />

              {/* Nombre max d'utilisations */}
              <Typography variant="subtitle1" sx={{ mb: 1 }}>Nombre maximal d'utilisations</Typography>
              <Space wrap size="middle">
                <Form.Item name="NbMaxUtilisations" label="Nombre max utilisations" rules={[{ required: true }]}>
                  <InputNumber min={0} style={{ width: 200 }} />
                </Form.Item>
                <SearchModal
                  Titre="Périodicité"
                  label="Périodicité"
                  name="Periodicite"
                  Parametre={searchPeriodicite}
                  form={formEveTout}
                  defaultCode={String(formEveTout.getFieldValue('Periodicite') ?? '')}
                  defaultLibelle={String(formEveTout.getFieldValue('lib_Periodicite') ?? '')}
                  width={12}
                  Taille1={167}
                  Taille2={320}
                  notRequired
                />
              </Space>
            </Form>
          )}
        </Modal>

        {/* Ajout / modification d'un événement couvert (catalogue de la catégorie du produit) */}
        <Modal
          title={eveEnEdition ? `Modifier l'événement ${eveEnEdition.Code}` : 'Ajouter un événement couvert'}
          open={eveDefOpen}
          onCancel={() => { setEveDefOpen(false); setEveEnEdition(null); }}
          onOk={() => formEveDef.submit()}
          okText="Enregistrer"
          cancelText="Fermer"
          confirmLoading={loading}
          zIndex={2100}
          className="safeCareModal"
          width={620}
          destroyOnClose
        >
          <Form form={formEveDef} layout="vertical" onFinish={enregistrerEve} preserve={false}>
            <Form.Item name="Code" label={`Événement du catalogue${categorie ? ` — ${categorie}` : ''}`} rules={[{ required: true, message: 'Choisissez un événement' }]}>
              <Select
                showSearch
                placeholder="Rechercher un événement"
                optionFilterProp="label"
                disabled={!!eveEnEdition}
                onChange={choisirCatalogue}
                getPopupContainer={(trigger) => trigger.parentElement ?? document.body}
                options={catalogueEve.map((c) => ({
                  value: c.CodeEvenement,
                  label: `${c.CodeEvenement} — ${c.LibelleEvenement}`,
                  disabled: !eveEnEdition && codesDejaAjoutes.has(c.CodeEvenement),
                }))}
              />
            </Form.Item>
            <Form.Item name="Libelle" label="Libellé" rules={[{ required: true, message: 'Libellé obligatoire' }]}>
              <Input disabled />
            </Form.Item>
            <Form.Item name="Description" label="Description">
              <Input.TextArea rows={3} placeholder="Description de la couverture" />
            </Form.Item>
            <Form.Item name="Ordre" label="Ordre d'affichage">
              <InputNumber min={0} max={999} style={{ width: '100%' }} />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    );
  };

  // ==================== ONGLET INTERRUPTION & RUPTURE ====================
  // Une seule barre d'action pilote les deux formulaires :
  //   Ajouter / Modifier  -> déverrouillent les deux formulaires
  //   Enregistrer         -> enregistre les données des deux formulaires
  //                          (chaque formulaire déjà rempli ou modifié est soumis)
  //   Annuler             -> recharge les valeurs enregistrées et verrouille
  const InterruptionRuptureTab = () => {
    const hasInt = interruptionRes?.StatutCode === 200 && !!interruptionRes.Result?.Table1?.[0];
    const hasRup = ruptureRes?.StatutCode === 200 && !!ruptureRes.Result?.Table1?.[0];
    const editing = stateInt.editMode || stateRup.editMode;

    const stateIR: ActionState = {
      ajout: editing || (hasInt && hasRup),
      modif: editing || (!hasInt && !hasRup),
      enreg: !editing,
      annul: !editing,
      editMode: editing,
    };

    const unlockBoth = () => {
      if (idVersion) {
        formInt.setFieldValue('IdVersion', idVersion);
        formRup.setFieldValue('IdVersion', idVersion);
      }
      const s: ActionState = { ajout: true, modif: true, enreg: false, annul: false, editMode: true };
      setStateInt(s);
      setStateRup(s);
    };

    const formAContenu = (form: FormInstance, champs: string[]) =>
      form.getFieldValue('IdRegleInterruption') || form.getFieldValue('IdRegleRupture') ||
      champs.some((c) => {
        const v = form.getFieldValue(c);
        return v !== undefined && v !== null && v !== '';
      });

    const enregistrerTout = () => {
      const submitInt =
        formAContenu(formInt, ['NbAvertissement', 'NbSuspension', 'NbResiliation', 'DelaiRegularisationJours']);
      const submitRup =
        formAContenu(formRup, ['ModeRupture', 'ConditionsRupture', 'NbImpayesRupture', 'ActionApresRupture']);
      if (!submitInt && !submitRup) {
        SweetAlert.fire({ title: 'Attention', text: 'Renseignez au moins une règle avant d\'enregistrer.', icon: 'warning' });
        return;
      }
      if (submitInt) formInt.submit();
      if (submitRup) formRup.submit();
    };

    const annulerTout = () => {
      applyRegle(interruptionRes, formInt, setStateInt);
      applyRegle(ruptureRes, formRup, setStateRup);
    };

    return (
    <div>
      <ActionBar
        state={stateIR}
        onAjouter={unlockBoth}
        onModifier={unlockBoth}
        onEnregistrer={enregistrerTout}
        onAnnuler={annulerTout}
      />
      <Form form={formInt} layout="vertical" onFinish={saveInt} disabled={!editing}>
        <Box fontStyle="inherit" width="85%" sx={{ p: 3, mb: 3 }}>
          <legend style={{ marginBottom: 16 }}>Règles d'interruption (impayés)</legend>
          <Form.Item name="IdRegleInterruption" hidden><input type="hidden" /></Form.Item>
          <Form.Item name="IdVersion" hidden><input type="hidden" /></Form.Item>
          <Space wrap size="middle">
            <Form.Item name="NbAvertissement" label="Nb échéances impayées → avertissement">
              <InputNumber min={1} max={12} />
            </Form.Item>
            <Form.Item name="NbSuspension" label="Nb échéances impayées → suspension">
              <InputNumber min={1} max={12} />
            </Form.Item>
            <Form.Item name="NbResiliation" label="Nb échéances impayées → résiliation">
              <InputNumber min={1} max={12} />
            </Form.Item>
            <Form.Item name="DelaiRegularisationJours" label="Délai de régularisation (jours)">
              <InputNumber min={0} max={365} />
            </Form.Item>
          </Space>
        </Box>
      </Form>
      <Form form={formRup} layout="vertical" onFinish={saveRup} disabled={!editing}>
        <Box fontStyle="inherit" width="85%" sx={{ p: 3, mb: 3 }}>
          <legend style={{ marginBottom: 16 }}>Règles de rupture</legend>
          <Form.Item name="IdRegleRupture" hidden><input type="hidden" /></Form.Item>
          <Form.Item name="IdVersion" hidden><input type="hidden" /></Form.Item>
          <Space wrap size="middle">
            <Form.Item name="ModeRupture" label="Mode de rupture" rules={[{ required: true }]}>
              <Select style={{ width: 250 }}>
                <Select.Option value="AUTOMATIQUE">Automatique</Select.Option>
                <Select.Option value="MANUEL">Manuel</Select.Option>
              </Select>
            </Form.Item>
            {/* Conditions de rupture : seulement en mode MANUEL */}
            <Form.Item noStyle shouldUpdate={(prev, cur) => prev.ModeRupture !== cur.ModeRupture}>
              {({ getFieldValue }) =>
                getFieldValue('ModeRupture') === 'MANUEL' ? (
                  <Form.Item name="ConditionsRupture" label="Conditions de rupture">
                    <Input style={{ width: 300 }} placeholder="Ex: Impayés, demande client" />
                  </Form.Item>
                ) : getFieldValue('ModeRupture') === 'AUTOMATIQUE' ? (
                  <Form.Item name="NbImpayesRupture" label="Nombre d'impayés déclenchant la rupture" rules={[{ required: true, message: 'Seuil requis' }]}>
                    <InputNumber min={1} max={120} style={{ width: 250 }} placeholder="Ex: 3" />
                  </Form.Item>
                ) : null
              }
            </Form.Item>
            <Form.Item name="ActionApresRupture" label="Action après rupture">
              <Select style={{ width: 250 }}>
                <Select.Option value="CLOTURE">Clôture du contrat</Select.Option>
                <Select.Option value="SUSPENSION">Suspension</Select.Option>
                <Select.Option value="RENOUVELLEMENT">Renouvellement</Select.Option>
              </Select>
            </Form.Item>
          </Space>
        </Box>
      </Form>
    </div>
    );
  };

  // ==================== ONGLET DOCUMENTS REQUIS ====================
  const columnsDocReq: ColumnsType<DocumentRequis> = [
    { title: 'Ordre', dataIndex: 'Ordre', width: 70 },
    { title: 'Libellé', dataIndex: 'LibelleDocument', width: 200 },
    { title: 'Description', dataIndex: 'Description', ellipsis: true },
    {
      title: 'Obligatoire',
      dataIndex: 'Obligatoire',
      width: 110,
      render: (v: boolean) => (v ? 'Oui' : 'Non'),
    },
  ];

  const renderDocReqTab = (
    ops: ReturnType<typeof makeDocReqOps>,
    formX: any,
    stateX: ActionState,
    selectedX: DocumentRequis | null,
    list: DocumentRequis[],
    legend: string,
    exemple: string,
    contexte: 'INDEMNISATION' | 'RUPTURE',
  ) => {
    const colsDocReq: ColumnsType<DocumentRequis> = contexte === 'INDEMNISATION'
      ? [
          { title: 'Ordre', dataIndex: 'Ordre', width: 70 },
          { title: 'Libellé', dataIndex: 'LibelleDocument', width: 200 },
          {
            title: 'Événement',
            dataIndex: 'LibelleEvenement',
            width: 160,
            render: (v: string | undefined) => v ?? 'Tous',
          },
          { title: 'Description', dataIndex: 'Description', ellipsis: true },
          {
            title: 'Obligatoire',
            dataIndex: 'Obligatoire',
            width: 110,
            render: (v: boolean) => (v ? 'Oui' : 'Non'),
          },
        ]
      : columnsDocReq;
    return (
    <Form
      form={formX}
      layout="vertical"
      onFinish={ops.save}
      disabled={!stateX.editMode}
    >
      <ActionBar
        state={stateX}
        onAjouter={ops.h.ajouter}
        onModifier={ops.h.modifier}
        onEnregistrer={ops.h.enregistrer}
        onAnnuler={ops.h.annuler}
      />
      <div className="clientP">
        <div className="tabItem">
          <div>
            <Box fontStyle="inherit" width="85%" position="absolute" sx={{ p: 3 }}>
              <legend style={{ marginBottom: 16 }}>{legend}</legend>
              <Space wrap size="middle">
                <Form.Item name="IdDocumentRequis" hidden><input type="hidden" /></Form.Item>
                <Form.Item name="IdVersion" hidden><input type="hidden" /></Form.Item>
                <Form.Item
                  name="LibelleDocument"
                  label="Libellé du document"
                  rules={[{ required: true, message: 'Libellé obligatoire' }]}
                >
                  <Input placeholder={exemple} style={{ width: 300 }} />
                </Form.Item>
                {contexte === 'INDEMNISATION' && (
                  <Form.Item name="IdEvenement" label="Événement couvert">
                    <Select
                      style={{ width: 220 }}
                      allowClear
                      placeholder="Tous les événements (commun)"
                      options={evenements.map((e) => ({
                        value: e.IdEvenement,
                        label: `${e.Code} — ${e.Libelle}`,
                      }))}
                    />
                  </Form.Item>
                )}
                <Form.Item name="Ordre" label="Ordre d'affichage">
                  <InputNumber min={0} max={100} />
                </Form.Item>
                <Form.Item name="Obligatoire" label="Obligatoire" valuePropName="checked">
                  <Select
                    style={{ width: 120 }}
                    options={[
                      { value: true, label: 'Oui' },
                      { value: false, label: 'Non' },
                    ]}
                  />
                </Form.Item>
              </Space>
              <Form.Item name="Description" label="Description (optionnel)">
                <Input.TextArea rows={2} placeholder="Détails sur le document attendu..." style={{ width: '100%' }} />
              </Form.Item>
              {selectedX && stateX.editMode && (
                <Button danger size="small" onClick={ops.onDelete} style={{ marginTop: 8 }}>
                  Supprimer le document sélectionné
                </Button>
              )}
              <div style={{ marginTop: 16 }}>
                <Table<DocumentRequis>
                  size="small"
                  rowKey="IdDocumentRequis"
                  columns={colsDocReq}
                  dataSource={list}
                  pagination={false}
                  scroll={{ y: 200 }}
                  onRow={(record) => ({
                    onClick: () => ops.onSelect(record),
                    style: { cursor: 'pointer' },
                  })}
                  rowClassName={(record) =>
                    record.IdDocumentRequis === selectedX?.IdDocumentRequis ? 'ant-table-row-selected' : ''
                  }
                />
              </div>
            </Box>
          </div>
        </div>
      </div>
    </Form>
    );
  };

  const items: TabsProps['items'] = [
    { key: '2', label: 'Cotisation', children: CotisationTab() },
    { key: '3', label: 'Éligibilité & Carence', children: EligibiliteCarenceTab() },
    { key: '5', label: 'Événements & Règles', children: EvenementRegleTab() },
    { key: '6', label: 'Interruption & Rupture', children: InterruptionRuptureTab() },
    { key: '8', label: 'Docs indemnisation', children: renderDocReqTab(docReqIndem, formDocReq, stateDocReq, selectedDocReq, documentsRequisIndem, 'Documents requis — indemnisation', 'Ex : Acte de naissance', 'INDEMNISATION') },
    { key: '9', label: 'Docs suspension', children: renderDocReqTab(docReqRup, formDocReqRup, stateDocReqRup, selectedDocReqRup, documentsRequisRup, 'Documents requis — suspension / rupture', 'Ex : Lettre de résiliation', 'RUPTURE') },
  ];

  if (open === false) {
    return null;
  }

  if (!idVersion) {
    return (
      <div className="tab">
        <Typography align="center" className="formTitle">
          <b>Configuration de version</b>
        </Typography>
        <Box sx={{ p: 5, textAlign: 'center' }}>
          <Typography color="error">
            Aucune version sélectionnée. Veuillez sélectionner une version dans
            l'administration des produits, puis cliquer sur "Configurer".
          </Typography>
        </Box>
      </div>
    );
  }

  return (
    <SafeCarePageModal
      open={open}
      title={`Configuration de la version #${idVersion}`}
      onBack={onBack ?? (() => navigate('/assurance/rupture'))}
    >
      <div className="tab" style={{ position: 'relative' }}>
        <Spin spinning={loading} size="large" style={{ color: '#116599' }}>
          <div className="clientP">
            <div>
              <Tabs
                size="small"
                type="card"
                activeKey={activeTab}
                onChange={setActiveTab}
                defaultActiveKey="1"
                items={items}
                animated={{ inkBar: true, tabPane: true }}
              />
            </div>
          </div>
        </Spin>
      </div>
    </SafeCarePageModal>
  );
};

export default ConfigurationVersion;